import { type JSX } from 'react';
import { abbreviate } from '@chainflip/utils/string';
import classNames from 'classnames';
import BaseNavbar, { type NavItemProps } from '@/shared/components/BaseNavbar';
import Button from '@/shared/components/flip-ui-kit/Button';
import { ConnectAvatar } from '@/shared/components/RainbowKit';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import BoltIcon from '@/shared/icons/flip-ui-kit/large/Bolt';
import ChartIcon from '@/shared/icons/flip-ui-kit/large/Chart';
import GiveCoinsIcon from '@/shared/icons/flip-ui-kit/large/GiveCoins';
import GridIcon from '@/shared/icons/flip-ui-kit/large/Grid';
import LayersIcon from '@/shared/icons/flip-ui-kit/large/Layers';
import PiggyIcon from '@/shared/icons/flip-ui-kit/large/Piggy';
import PowerIcon from '@/shared/icons/flip-ui-kit/large/Power';
import TargetIcon from '@/shared/icons/flip-ui-kit/large/Target';
import { FLIP_SYMBOL, STAKING_URL } from '@/shared/utils/env';
import { MiscNavDropdownLinks } from './MiscNavDropdownLinks';
import { PolkaConnectInfo } from './PolkaConnectInfo';
import useDisconnectWithWarning from '../hooks/useDisconnectWithWarning';

const navItems: NavItemProps[] = [
  {
    type: 'dropdown',
    display: 'desktop',
    title: 'Portfolio',
    items: [
      {
        label: 'Overview',
        href: '/portfolio',
        icon: <GridIcon />,
      },
      {
        label: 'Balances',
        href: '/balances',
        icon: <PiggyIcon />,
      },
    ],
  },
  {
    type: 'dropdown',
    display: 'desktop',
    title: 'Trading',
    items: [
      {
        label: 'Markets',
        href: '/market/btc',
        activePath: '/market',
        icon: <ChartIcon />,
      },
      {
        label: 'Orders & Activity',
        href: '/orders',
        icon: <LayersIcon />,
      },
    ],
  },
  {
    type: 'link',
    display: 'desktop',
    children: 'Lending',
    href: '/lending',
    newBadge: true,
  },
  {
    type: 'link',
    display: 'desktop',
    href: '/swap',
    children: 'Swap',
  },
  {
    type: 'dropdown',
    display: 'desktop',
    title: 'Earn',
    items: [
      {
        label: `Stake ${FLIP_SYMBOL}`,
        href: new URL('/delegate', STAKING_URL).toString(),
        icon: <GiveCoinsIcon />,
      },
      {
        label: 'Boost',
        href: '/boost/positions',
        activePath: '/boost',
        icon: <BoltIcon />,
      },
      {
        label: 'Stablecoin Strategies',
        href: '/strategies/positions',
        activePath: '/strategies',
        icon: <TargetIcon />,
      },
    ],
  },
];

const Navbar = ({
  logoHref,
  isMobileView = false,
  noContainer = false,
}: {
  logoHref: string;
  isMobileView?: boolean;
  noContainer?: boolean;
}): JSX.Element => {
  const { selectedAccount } = usePolkadot();
  const { disconnect, disconnectWarning } = useDisconnectWithWarning();
  const address = selectedAccount?.idSs58;
  return (
    <BaseNavbar
      className={classNames(isMobileView && '!z-0', 'border-b border-b-cf-gray-3')}
      navItems={navItems}
      logoHref={logoHref}
      hideNetworkBadgeOnMdDevices
      actions={
        !isMobileView && (
          <>
            <PolkaConnectInfo />
            <MiscNavDropdownLinks />
          </>
        )
      }
      hamburgerReplacement={
        isMobileView && address ? (
          <div className="flex items-center gap-x-2">
            <Button
              type="secondary-standard"
              className="pointer-events-none flex items-center gap-x-2 whitespace-nowrap bg-transparent"
            >
              {abbreviate(address)}
              <ConnectAvatar address={address} size={16} />
            </Button>
            <Button
              type="secondary-standard"
              onClick={disconnect}
              className="flex !min-w-[40px] items-center bg-transparent !p-0"
            >
              <PowerIcon />
            </Button>
            {disconnectWarning}
          </div>
        ) : null
      }
      noContainer={noContainer}
    />
  );
};

export default Navbar;
